import axios from "axios";

// getCarrierElig runs one DynamoDB query per class code, so a large quote can
// approach API Gateway's 29s limit. Split the body into small requests, run a
// few at a time, and merge the responses back into the single-request shape.
const CLASS_CODES_PER_REQUEST = 5;
const MAX_PARALLEL_REQUESTS = 5;
const MAX_ATTEMPTS = 3;
const BASE_RETRY_DELAY_MS = 1000;

// Same precedence the backend applies within one request: no > uw > yes.
const ELIGIBILITY_PRIORITY = { no: 3, uw: 2, yes: 1 };

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isRetryable = (error) => {
  let status = error?.response?.status;
  // No response means a network error or gateway timeout.
  return !status || status === 429 || status >= 500;
};

const postWithRetry = async (url, body) => {
  for (let attempt = 1; ; attempt++) {
    try {
      return await axios.post(url, JSON.stringify(body));
    } catch (error) {
      if (attempt >= MAX_ATTEMPTS || !isRetryable(error)) throw error;
      // 1s, 2s, ... plus jitter so parallel batches don't retry in lockstep.
      let delay =
        BASE_RETRY_DELAY_MS * 2 ** (attempt - 1) + Math.random() * 250;
      await sleep(delay);
    }
  }
};

// { location: [classCode, ...] } -> [{ location: [up to N classCodes] }, ...]
export const splitCarrierEligBody = (carrierEligBody) => {
  let entries = [];
  for (let location in carrierEligBody) {
    for (let classCodeData of carrierEligBody[location]) {
      entries.push([location, classCodeData]);
    }
  }

  let batches = [];
  for (let i = 0; i < entries.length; i += CLASS_CODES_PER_REQUEST) {
    let batch = {};
    for (let [location, classCodeData] of entries.slice(
      i,
      i + CLASS_CODES_PER_REQUEST
    )) {
      if (!(location in batch)) batch[location] = [];
      batch[location].push(classCodeData);
    }
    batches.push(batch);
  }
  return batches;
};

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const deepMerge = (target, source) => {
  for (let key in source) {
    if (isPlainObject(source[key]) && isPlainObject(target[key])) {
      deepMerge(target[key], source[key]);
    } else {
      target[key] = source[key];
    }
  }
  return target;
};

// Combine batch responses, in batch order, into one getCarrierElig response.
export const mergeCarrierEligResponses = (responses) => {
  let merged = { eligibility: {}, industries: {}, details: {} };

  for (let response of responses) {
    for (let location in response.eligibility || {}) {
      if (!(location in merged.eligibility)) {
        merged.eligibility[location] = {};
      }
      for (let carrier in response.eligibility[location]) {
        let incoming = response.eligibility[location][carrier];
        let existing = merged.eligibility[location][carrier];

        if (!existing) {
          merged.eligibility[location][carrier] = {
            value: incoming.value,
            excludedList: [...(incoming.excludedList || [])],
            classCodeEligibility: { ...incoming.classCodeEligibility },
          };
          continue;
        }

        if (
          (ELIGIBILITY_PRIORITY[incoming.value] || 0) >
          (ELIGIBILITY_PRIORITY[existing.value] || 0)
        ) {
          existing.value = incoming.value;
        }
        existing.excludedList.push(...(incoming.excludedList || []));
        Object.assign(
          existing.classCodeEligibility,
          incoming.classCodeEligibility
        );
      }
    }

    deepMerge(merged.industries, response.industries || {});
    deepMerge(merged.details, response.details || {});
  }

  return merged;
};

// Drop-in for axios.post(url, body): resolves to { data } with the merged
// response, and rejects if any batch still fails after retries, since a
// partial result could hide a carrier exclusion.
export const fetchCarrierEligibility = async (url, carrierEligBody) => {
  let batches = splitCarrierEligBody(carrierEligBody);
  if (batches.length <= 1) {
    return postWithRetry(url, carrierEligBody);
  }

  let responses = new Array(batches.length);
  let next = 0;
  let worker = async () => {
    while (next < batches.length) {
      let i = next++;
      responses[i] = (await postWithRetry(url, batches[i])).data;
    }
  };
  await Promise.all(
    Array.from(
      { length: Math.min(MAX_PARALLEL_REQUESTS, batches.length) },
      worker
    )
  );

  return { data: mergeCarrierEligResponses(responses) };
};
